import React, {
    FC,
    useMemo,
    useRef,
    useEffect,
    useCallback,
    forwardRef,
    useImperativeHandle,
    RefObject,
    MutableRefObject,
} from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import * as Three from 'three';
import {
    Scene, WebGLRenderer, PerspectiveCamera, Fog, Group,
} from 'three';
import { OrbitControls } from '@app/react/uikit/OrbitControls';
import {
    createRenderer,
    createLight,
    createCamera,
    createOrbitControl,
} from '@app/react/3d/utils/order3d';
import { ResizeEventHandler } from '@app/react/uikit/ResizeEventHandler';
import { ZoomButton } from '@app/react/uikit/ZoomButton';
import { DimensionToggleButton } from '@app/react/uikit/DimensionToggleButton';
import {
    KintekiArrowModel,
    EntekiArrowModel,
    MakiwaraArrowModel,
} from '@app/react/3d/ArrowModelInterface';
import { useOrderPathParams } from '@app/react/contexts/useOrderPathParams';

import { KINTEKI, ENTEKI, MAKIWARA } from '@app/react/const/type';
import type { AppState } from '@app/react/reducer';
import { useMediaDesktop, useMediaMobile } from '@app/react/common/useMedia';

import {
    kintekiModel,
    entekiModel,
    makiwaraModel,
} from '@app/react/reducer/OrdermadeArrowModelReducer';
import { useAnimationFrame } from '@app/react/3d/useAnimationFrame';
import { setupAnimation } from '@app/react/3d/setupAnimation';
import { execAnimation } from '@app/react/3d/execAnimation';

type AllModelRet = KintekiArrowModel | EntekiArrowModel | MakiwaraArrowModel | null;

const selectCurrentView = (state: AppState): string | null => {
    const { open, partsName } = state.pageOpenDetection;
    return open ? partsName : null;
};

const setupThreejs = (
    container: RefObject<HTMLCanvasElement>,
    rendererRef: MutableRefObject<WebGLRenderer | null>,
    cameraRef: MutableRefObject<PerspectiveCamera | null>,
    controlRef: MutableRefObject<OrbitControls | null>,
): void => {
    if (container.current === null) {
        return;
    }
    // 500 is 適当なデフォルト値
    const width = container.current?.clientWidth || 500;
    const height = container.current?.clientHeight || 500;
    console.log('[3d] renderer size:', width, height);

    const renderer = createRenderer(
        container.current,
        width,
        height,
        window.devicePixelRatio,
    );
    rendererRef.current = renderer;

    const camera = createCamera(width, height);
    cameraRef.current = camera;

    const controls = createOrbitControl(camera, renderer.domElement);
    controlRef.current = controls;
    // enable 2d mode
    controlRef.current.enableRotate = false;
    controlRef.current.enablePan = true;
};

const resizeHandler = (
    container: RefObject<HTMLCanvasElement>,
    rendererRef: RefObject<WebGLRenderer>,
    cameraRef: RefObject<PerspectiveCamera>,
): void => {
    const width = container.current?.clientWidth || 500;
    const height = container.current?.clientHeight || 500;
    console.log('[3d] resize renderer size:', width, height);

    rendererRef.current?.setSize(width, height, false);
    if (cameraRef.current) {
        cameraRef.current.aspect = width / height;
    }
    cameraRef.current?.updateProjectionMatrix();
};

export interface SceneHander {
    setModel: (model: Group) => void;
    addModel: (model: Group) => void;
    removeModel: () => void;
    control: () => OrbitControls | null;
}

interface CtrlPanelProps {
    visibility: boolean;
    zoomIn: () => void;
    zoomOut: () => void;
    on3d: () => void;
    on2d: () => void;
}

const CtrlPanel: FC<CtrlPanelProps> = (props) => {
    const {
        visibility, zoomIn, zoomOut, on3d, on2d,
    } = props;

    const className = classNames('ctrl-panel', {
        hidden: !visibility,
    });

    return (
        <div className={className}>
            <DimensionToggleButton onClick3d={on3d} onClick2d={on2d} defaultValue={false} />
            <ZoomButton zoomIn={zoomIn} zoomOut={zoomOut} />
        </div>
    );
};

interface Order3dProps {
    className?: string;
    showCtrlPanel: boolean;
}

export const Order3d: React.ForwardRefRenderFunction<SceneHander, Order3dProps> = (props, ref) => {
    const { className, showCtrlPanel } = props;

    const container = useRef<HTMLCanvasElement>(null);
    const controlRef = useRef<OrbitControls | null>(null);
    const cameraRef = useRef<PerspectiveCamera | null>(null);
    const modelRef = useRef<Three.Group | null>(null);
    const rendererRef = useRef<WebGLRenderer | null>(null);
    const { width, height } = ResizeEventHandler();

    const scene = useMemo(() => {
        const s = new Scene();
        s.fog = new Fog(0xa0a0a0, 200, 1000);
        return s;
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            setModel: (model) => {
                if (modelRef.current == null) {
                    modelRef.current = model;
                    scene.add(model);
                }
            },
            addModel: (model) => {
                if (modelRef.current != null) {
                    console.log('[3d] replace model via add model');
                    scene.remove(modelRef.current);
                }
                modelRef.current = model;
                scene.add(model);
            },
            removeModel: () => {
                if (modelRef.current != null) {
                    console.log('[3d] remove model');
                    scene.remove(modelRef.current);
                    modelRef.current = null;
                }
            },
            control: () => controlRef.current,
        }),
        [modelRef, scene, controlRef],
    );

    // light
    useMemo(() => {
        const {
            hemisphereLight: h,
            ambientLight: ambLight,
            directionalLights: dirLights,
        } = createLight();

        scene.add(h);
        scene.add(ambLight);
        dirLights.forEach((l) => scene.add(l));
    }, [scene]);

    useEffect(() => {
        setupThreejs(container, rendererRef, cameraRef, controlRef);
    }, [container, rendererRef, cameraRef, controlRef]);

    useEffect(() => {
        resizeHandler(container, rendererRef, cameraRef);
    }, [container, rendererRef, width, height]);

    const loop = useCallback(() => {
        controlRef.current?.update();
        if (cameraRef.current) {
            rendererRef.current?.render(scene, cameraRef.current);
        }
    }, [controlRef, cameraRef, rendererRef, scene]);

    useAnimationFrame(loop, 30);

    const zoomIn = useCallback(() => controlRef.current?.dollyIn(), [
        controlRef,
    ]);
    const zoomOut = useCallback(() => controlRef.current?.dollyOut(), [
        controlRef,
    ]);
    const on3d = useCallback(() => {
        if (controlRef.current === null) {
            return;
        }
        controlRef.current.enableRotate = true;
        controlRef.current.enablePan = false;
    }, [controlRef]);
    const on2d = useCallback(() => {
        if (controlRef.current === null) {
            return;
        }
        controlRef.current.enableRotate = false;
        controlRef.current.enablePan = true;
    }, [controlRef]);

    return (
        <div className={classNames('order_3d_img')}>
            <canvas
                className={classNames('preview', className)}
                ref={container}
            />
            <CtrlPanel
                visibility={showCtrlPanel}
                zoomIn={zoomIn}
                zoomOut={zoomOut}
                on3d={on3d}
                on2d={on2d}
            />
        </div>
    );
};

export const Order3dRef = forwardRef<SceneHander, Order3dProps>(Order3d);

export const Order3dContainer: FC = () => {
    const modelRef = useRef<KintekiArrowModel | EntekiArrowModel | MakiwaraArrowModel | null>(null);
    const modelHandleRef = useRef<SceneHander>(null);

    const { arrowType } = useOrderPathParams();
    const handler = ((at) => {
        switch (at) {
            case KINTEKI:
                return (state: AppState) => [state.model.kinteki, kintekiModel];
            case ENTEKI:
                return (state: AppState) => [state.model.enteki, entekiModel];
            case MAKIWARA:
                return (state: AppState) => [state.model.makiwara, makiwaraModel];
            default:
                return () => [false, null];
        }
    })(arrowType);

    const [,arrowModel] = useSelector<AppState, [boolean, AllModelRet]>(handler);
    modelRef.current = arrowModel;

    const isDesktop = useMediaDesktop();
    const isMobile = useMediaMobile();
    const openedParts = useSelector(selectCurrentView);
    const isPreviewMode = useSelector<AppState, boolean>(
        (state) => state.uiPartsView.previewMode,
    );
    const isKamonKokuin = openedParts === 'familycrest' || openedParts === 'carving';
    const isPreview = isMobile ? isPreviewMode : false;
    const showCtrlPnale = isDesktop ? true : isPreview;
    const animation = useMemo(() => (
        setupAnimation(modelRef, modelHandleRef)
    ), [modelRef, modelHandleRef]);

    useEffect(() => {
        execAnimation(isDesktop, isMobile, openedParts, animation, isPreviewMode);
    }, [openedParts, isDesktop, isMobile, animation, isPreviewMode, arrowModel]);

    useEffect(() => {
        if (arrowModel != null) {
            modelHandleRef.current?.setModel(arrowModel.model);
        }
    }, [arrowModel]);

    return (
        <Order3dRef
            ref={modelHandleRef}
            showCtrlPanel={showCtrlPnale}
            className={classNames({
                'parts-center-mode':
                    isMobile && !isKamonKokuin && !isPreviewMode,
            })}
        />
    );
};
